import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, FileText, Calendar, FileSpreadsheet, FileDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FinancialReports = () => {
  const auth = useAuth();
  const token = auth?.token;
  const { selectedBranch } = useApp();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('monthly');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    if (token) {
      fetchReport();
    }
  }, [period, year, month, token, selectedBranch]);

  const fetchReport = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        period,
        year: year.toString()
      });
      if (period === 'monthly') {
        params.append('month', month.toString());
      }
      if (selectedBranch && selectedBranch !== 'all') {
        params.append('branch_id', selectedBranch);
      }
      
      const response = await axios.get(`${API_URL}/api/v2/financial-report?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Gagal memuat laporan');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const monthNames = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
  ];

  // ==================== EXPORT HELPERS ====================
  const fmtRupiah = (n) => 'Rp ' + (Number(n) || 0).toLocaleString('id-ID');
  const periodLabel = () => period === 'yearly'
    ? `Tahunan ${year}`
    : `${monthNames[month - 1]} ${year}`;
  const branchLabel = () => (selectedBranch && selectedBranch !== 'all') ? `Cabang ${selectedBranch}` : 'Semua Cabang';

  const exportToExcel = () => {
    if (!report) { toast.error('Data belum termuat'); return; }
    const cd = Array.isArray(report.chart_data) ? report.chart_data : [];
    const sm = report.summary || {};
    const stamp = new Date().toISOString().slice(0, 10);
    const lines = [];
    lines.push('"Laporan Keuangan"');
    lines.push(`"Periode","${periodLabel()}"`);
    lines.push(`"Cabang","${branchLabel()}"`);
    lines.push(`"Dicetak","${new Date().toLocaleString('id-ID')}"`);
    lines.push('');
    lines.push('"RINGKASAN"');
    lines.push(`"Total Pendapatan",${sm.total_income || 0}`);
    lines.push(`"Total Pengeluaran",${sm.total_expenses || 0}`);
    lines.push(`"${(sm.net_profit || 0) >= 0 ? 'Laba Bersih' : 'Rugi Bersih'}",${sm.net_profit || 0}`);
    lines.push('');
    if (cd.length > 0) {
      lines.push(`"RINCIAN ${period === 'yearly' ? 'BULANAN' : 'HARIAN'}"`);
      lines.push('"Periode","Pendapatan","Pengeluaran","Selisih"');
      cd.forEach(d => {
        const label = d.name || d.month || d.date || '';
        const inc = d.Pendapatan || 0;
        const exp = d.Pengeluaran || 0;
        lines.push(`"${label}",${inc},${exp},${inc - exp}`);
      });
      const tinc = cd.reduce((a, d) => a + (d.Pendapatan || 0), 0);
      const texp = cd.reduce((a, d) => a + (d.Pengeluaran || 0), 0);
      lines.push(`"TOTAL",${tinc},${texp},${tinc - texp}`);
    }
    const csv = "\ufeff" + lines.join("\n");
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `laporan-keuangan-${period}-${year}${period==='monthly'?('-'+String(month).padStart(2,'0')):''}-${stamp}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success('Laporan Keuangan berhasil di-export ke Excel/CSV');
  };

  const exportToPdf = () => {
    if (!report) { toast.error('Data belum termuat'); return; }
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup diblokir browser'); return; }
    const cd = Array.isArray(report.chart_data) ? report.chart_data : [];
    const sm = report.summary || {};
    const cell = 'border:1px solid #cbd5e1;padding:6px 8px;font-size:11px';
    const cellHead = 'border:1px solid #cbd5e1;padding:6px 8px;background:#f1f5f9;text-align:left;font-size:11px';
    const netColor = (sm.net_profit || 0) >= 0 ? '#065f46' : '#991b1b';
    const netBg = (sm.net_profit || 0) >= 0 ? '#ecfdf5' : '#fef2f2';
    const netBorder = (sm.net_profit || 0) >= 0 ? '#10b981' : '#ef4444';
    const summaryHTML = `
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin:14px 0">
        <div style="padding:10px 14px;background:#ecfdf5;border-left:4px solid #10b981;border-radius:4px">
          <div style="font-size:11px;color:#6b7280">Total Pendapatan</div>
          <div style="font-size:16px;font-weight:600;color:#065f46">${fmtRupiah(sm.total_income)}</div>
        </div>
        <div style="padding:10px 14px;background:#fef2f2;border-left:4px solid #ef4444;border-radius:4px">
          <div style="font-size:11px;color:#6b7280">Total Pengeluaran</div>
          <div style="font-size:16px;font-weight:600;color:#991b1b">${fmtRupiah(sm.total_expenses)}</div>
        </div>
        <div style="padding:10px 14px;background:${netBg};border-left:4px solid ${netBorder};border-radius:4px">
          <div style="font-size:11px;color:#6b7280">${(sm.net_profit || 0) >= 0 ? 'Laba Bersih' : 'Rugi Bersih'}</div>
          <div style="font-size:16px;font-weight:600;color:${netColor}">${fmtRupiah(sm.net_profit)}</div>
        </div>
      </div>`;
    let breakdownHTML = '';
    if (cd.length > 0) {
      const tinc = cd.reduce((a, d) => a + (d.Pendapatan || 0), 0);
      const texp = cd.reduce((a, d) => a + (d.Pengeluaran || 0), 0);
      breakdownHTML = `
        <h3 style="margin:20px 0 8px 0;font-size:13px">Rincian ${period === 'yearly' ? 'Bulanan' : 'Harian'}</h3>
        <table style="width:100%;border-collapse:collapse">
          <thead>
            <tr>
              <th style="${cellHead}">Periode</th>
              <th style="${cellHead};text-align:right">Pendapatan</th>
              <th style="${cellHead};text-align:right">Pengeluaran</th>
              <th style="${cellHead};text-align:right">Selisih</th>
            </tr>
          </thead>
          <tbody>
            ${cd.map(d => {
              const label = d.name || d.month || d.date || '';
              const inc = d.Pendapatan || 0;
              const exp = d.Pengeluaran || 0;
              return `<tr>
                <td style="${cell}">${label}</td>
                <td style="${cell};text-align:right">${fmtRupiah(inc)}</td>
                <td style="${cell};text-align:right">${fmtRupiah(exp)}</td>
                <td style="${cell};text-align:right;color:${(inc-exp)>=0?'#065f46':'#991b1b'}">${fmtRupiah(inc - exp)}</td>
              </tr>`;
            }).join('')}
            <tr style="font-weight:bold;background:#f8fafc">
              <td style="${cell}">TOTAL</td>
              <td style="${cell};text-align:right">${fmtRupiah(tinc)}</td>
              <td style="${cell};text-align:right">${fmtRupiah(texp)}</td>
              <td style="${cell};text-align:right;color:${(tinc-texp)>=0?'#065f46':'#991b1b'}">${fmtRupiah(tinc - texp)}</td>
            </tr>
          </tbody>
        </table>`;
    }
    w.document.write(`<html><head><title>Laporan Keuangan</title></head>
      <body style="font-family:Arial,sans-serif;padding:24px;color:#0f172a">
        <div style="border-bottom:2px solid #6366f1;padding-bottom:10px;margin-bottom:10px">
          <h2 style="margin:0;color:#4338ca">Laporan Keuangan</h2>
          <div style="font-size:11px;color:#64748b;margin-top:4px">
            Periode: <b>${periodLabel()}</b> · ${branchLabel()}
          </div>
          <div style="font-size:10px;color:#94a3b8;margin-top:2px">Dicetak: ${new Date().toLocaleString('id-ID')}</div>
        </div>
        ${summaryHTML}
        ${breakdownHTML}
        <script>setTimeout(()=>window.print(),300)</script>
      </body></html>`);
    w.document.close();
    toast.success('Laporan Keuangan siap dicetak / disimpan sebagai PDF');
  };

  return (
    <div className="space-y-4 p-3 sm:p-4 md:p-6 bg-slate-50 dark:bg-slate-950 min-h-screen">
      {/* Header */}
      <Card className="border border-slate-200 dark:border-slate-800 shadow-none dark:bg-slate-900">
        <CardContent className="pt-4 pb-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
            <div>
              <h2 className="text-lg font-bold text-slate-800 dark:text-white">Laporan Keuangan</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Ringkasan Pendapatan & Pengeluaran
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
              {/* Period Selector */}
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200"
              >
                <option value="monthly">Bulanan</option>
                <option value="yearly">Tahunan</option>
              </select>

              {/* Year Selector */}
              <Input
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="w-20 sm:w-24 h-7 text-xs dark:bg-slate-700 dark:text-white dark:border-slate-600"
              />

              {/* Month Selector (only for monthly) */}
              {period === 'monthly' && (
                <select
                  value={month}
                  onChange={(e) => setMonth(parseInt(e.target.value))}
                  className="px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200"
                >
                  {monthNames.map((name, idx) => (
                    <option key={idx} value={idx + 1}>{name}</option>
                  ))}
                </select>
              )}

              {/* Export Excel */}
              <Button
                onClick={exportToExcel}
                variant="outline"
                size="sm"
                disabled={!report || loading}
                className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-400 dark:text-emerald-400 h-7 text-xs px-2 disabled:opacity-50 dark:hover:bg-emerald-900/30"
                data-testid="financial-export-excel-btn"
              >
                <FileSpreadsheet className="w-3 h-3 mr-1" />
                Excel
              </Button>
              {/* Export PDF */}
              <Button
                onClick={exportToPdf}
                variant="outline"
                size="sm"
                disabled={!report || loading}
                className="border-rose-600 text-rose-600 hover:bg-rose-50 dark:border-rose-400 dark:text-rose-400 h-7 text-xs px-2 disabled:opacity-50 dark:hover:bg-rose-900/30"
                data-testid="financial-export-pdf-btn"
              >
                <FileDown className="w-3 h-3 mr-1" />
                PDF
              </Button>
              {/* Refresh Button */}
              <Button
                onClick={fetchReport}
                variant="outline"
                size="sm"
                className="border-indigo-600 text-indigo-600 hover:bg-indigo-50 dark:border-indigo-400 dark:text-indigo-400 h-7 text-xs px-2 dark:hover:bg-indigo-900/30"
              >
                <Calendar className="w-3 h-3 mr-1" />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : report ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border border-slate-200 dark:border-slate-800 shadow-none bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
              <CardContent className="pt-4 pb-4 px-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Pendapatan</p>
                  <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
                    <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                </div>
                <p className="text-lg sm:text-2xl font-bold text-slate-800 dark:text-white">{formatCurrency(report.summary?.total_income)}</p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                  Periode: {report.period?.start_date} - {report.period?.end_date}
                </p>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 dark:border-slate-800 shadow-none bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
              <CardContent className="pt-4 pb-4 px-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Pengeluaran</p>
                  <div className="p-2 bg-rose-50 dark:bg-rose-900/30 rounded-lg">
                    <TrendingDown className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                  </div>
                </div>
                <p className="text-lg sm:text-2xl font-bold text-slate-800 dark:text-white">{formatCurrency(report.summary?.total_expenses)}</p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Pengeluaran operasional</p>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 dark:border-slate-800 shadow-none bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
              <CardContent className="pt-4 pb-4 px-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {(report.summary?.net_profit || 0) >= 0 ? 'Laba Bersih' : 'Rugi Bersih'}
                  </p>
                  <div className={`p-2 rounded-lg ${
                    (report.summary?.net_profit || 0) >= 0 
                      ? 'bg-blue-50 dark:bg-blue-900/30' 
                      : 'bg-amber-50 dark:bg-amber-900/30'
                  }`}>
                    <TrendingUp className={`w-4 h-4 ${
                      (report.summary?.net_profit || 0) >= 0 
                        ? 'text-blue-600 dark:text-blue-400' 
                        : 'text-amber-600 dark:text-amber-400'
                    }`} />
                  </div>
                </div>
                <p className={`text-lg sm:text-2xl font-bold ${
                  (report.summary?.net_profit || 0) >= 0 
                    ? 'text-emerald-600 dark:text-emerald-400' 
                    : 'text-rose-600 dark:text-rose-400'
                }`}>{formatCurrency(report.summary?.net_profit)}</p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Pendapatan - Pengeluaran</p>
              </CardContent>
            </Card>
          </div>

          {/* Chart Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Bar Chart */}
            <Card className="border border-slate-200 dark:border-slate-800 shadow-none bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
              <CardHeader className="border-b border-slate-200 dark:border-slate-700 py-3 px-4">
                <CardTitle className="text-sm font-semibold text-slate-800 dark:text-white">
                  {period === 'yearly' ? 'Pendapatan vs Pengeluaran per Bulan' : 'Pendapatan vs Pengeluaran per Hari'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2 sm:p-4">
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={report.chart_data && report.chart_data.length > 0 ? report.chart_data : [
                      {
                        name: period === 'monthly' ? monthNames[month - 1] : year.toString(),
                        Pendapatan: report.summary?.total_income || 0,
                        Pengeluaran: report.summary?.total_expenses || 0,
                      }
                    ]}
                    margin={{ top: 10, right: 10, left: -10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 10 }} />
                    <YAxis 
                      tick={{ fill: '#64748b', fontSize: 10 }} 
                      tickFormatter={(value) => `${(value / 1000000).toFixed(0)}jt`}
                    />
                    <Tooltip 
                      formatter={(value) => [`Rp ${value.toLocaleString('id-ID')}`, '']}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Bar dataKey="Pendapatan" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Pengeluaran" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Pie Chart */}
            <Card className="border border-slate-200 dark:border-slate-800 shadow-none bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
              <CardHeader className="border-b border-slate-200 dark:border-slate-700 py-3 px-4">
                <CardTitle className="text-sm font-semibold text-slate-800 dark:text-white">Komposisi Keuangan</CardTitle>
              </CardHeader>
              <CardContent className="p-2 sm:p-4">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Pendapatan', value: report.summary?.total_income || 0, color: '#10b981' },
                        { name: 'Pengeluaran', value: report.summary?.total_expenses || 0, color: '#f43f5e' },
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {[
                        { name: 'Pendapatan', value: report.summary?.total_income || 0, color: '#10b981' },
                        { name: 'Pengeluaran', value: report.summary?.total_expenses || 0, color: '#f43f5e' },
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [`Rp ${value.toLocaleString('id-ID')}`, '']}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
          {/* Daily Income Table - only show for monthly period */}
          {period === 'monthly' && report.chart_data && report.chart_data.length > 0 && (
            <Card className="border-0 shadow-lg dark:bg-slate-800">
              <CardHeader className="border-b border-slate-200 dark:border-slate-700 py-3 px-4">
                <CardTitle className="dark:text-white text-sm font-semibold flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Pendapatan Harian - {monthNames[month - 1]} {year}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto -mx-px">
                  <table className="w-full min-w-[520px]">
                    <thead className="bg-slate-50 dark:bg-slate-700">
                      <tr>
                        <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">Tanggal</th>
                        <th className="px-3 sm:px-4 py-2 sm:py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">Pendapatan</th>
                        <th className="px-3 sm:px-4 py-2 sm:py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">Pengeluaran</th>
                        <th className="px-3 sm:px-4 py-2 sm:py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">Laba</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                      {report.chart_data.map((day, index) => {
                        const profit = (day.Pendapatan || 0) - (day.Pengeluaran || 0);
                        return (
                          <tr key={index} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                            <td className="px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm text-slate-700 dark:text-slate-300 whitespace-nowrap">
                              {day.name}
                            </td>
                            <td className="px-4 py-2.5 text-xs sm:text-sm text-right text-emerald-600 dark:text-emerald-400 font-medium whitespace-nowrap">
                              {formatCurrency(day.Pendapatan || 0)}
                            </td>
                            <td className="px-4 py-2.5 text-xs sm:text-sm text-right text-rose-600 dark:text-rose-400 font-medium whitespace-nowrap">
                              {formatCurrency(day.Pengeluaran || 0)}
                            </td>
                            <td className={`px-4 py-2.5 text-xs sm:text-sm text-right font-medium whitespace-nowrap ${
                              profit >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-amber-600 dark:text-amber-400'
                            }`}>
                              {formatCurrency(profit)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-slate-100 dark:bg-slate-600">
                      <tr>
                        <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-200 whitespace-nowrap">Total</td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-right font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                          {formatCurrency(report.chart_data.reduce((sum, d) => sum + (d.Pendapatan || 0), 0))}
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-right font-bold text-rose-600 dark:text-rose-400 whitespace-nowrap">
                          {formatCurrency(report.chart_data.reduce((sum, d) => sum + (d.Pengeluaran || 0), 0))}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-blue-600 dark:text-blue-400">
                          {formatCurrency(report.chart_data.reduce((sum, d) => sum + ((d.Pendapatan || 0) - (d.Pengeluaran || 0)), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-0 shadow-lg dark:bg-slate-800">
            <CardHeader className="border-b border-slate-200 dark:border-slate-700 py-3 px-4">
              <CardTitle className="dark:text-white text-sm font-semibold">Detail Laporan</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
                  <span className="text-sm text-slate-600 dark:text-slate-300">Pendapatan Kotor</span>
                  <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(report.summary?.total_income)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
                  <span className="text-sm text-slate-600 dark:text-slate-300">Total Pengeluaran</span>
                  <span className="text-sm font-bold text-red-600 dark:text-red-400">
                    - {formatCurrency(report.summary?.total_expenses)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 bg-slate-50 dark:bg-slate-700 px-3 rounded-lg">
                  <span className="font-bold text-slate-800 dark:text-white">Laba/Rugi Bersih</span>
                  <span className={`text-lg font-bold ${
                    (report.summary?.net_profit || 0) >= 0 
                      ? 'text-emerald-600 dark:text-emerald-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {formatCurrency(report.summary?.net_profit)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="border-0 shadow-lg dark:bg-slate-800">
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400">Tidak ada data laporan</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FinancialReports;
